import APP_SETTINGS from './settings';
const KEYBOARD_PREVIEW_DATA_FRESHNESS_MODE = 'live-drag-worker-preview';
function isThenable(value) {
    return (!!value &&
        typeof value === 'object' &&
        typeof value.then === 'function');
}
export class KeyboardPreviewEditFunnel {
    constructor() {
        this.runningRequest = null;
        this.queuedRequest = null;
        this.commitTimer = null;
        this.pendingCommit = null;
        this.prepareChain = Promise.resolve();
        this.asyncPrepareCount = 0;
    }
    queue(request) {
        if (this.asyncPrepareCount === 0) {
            const prepareResult = request.prepare?.();
            if (isThenable(prepareResult)) {
                this.enqueuePrepare(request, prepareResult);
                return;
            }
            if (prepareResult === false) {
                this.clearMatchingCompileContext(request);
                return;
            }
            this.finishLocalPrepare(request);
            return;
        }
        this.enqueuePrepare(request);
    }
    scheduleCommit(commit) {
        this.pendingCommit = commit;
        if (this.commitTimer !== null) {
            window.clearTimeout(this.commitTimer);
        }
        this.commitTimer = window.setTimeout(() => {
            this.commitTimer = null;
            void this.flushPendingCommit();
        }, APP_SETTINGS.KEYBOARD_PREVIEW_COMMIT_DEBOUNCE);
    }
    hasPendingWork() {
        return !!(this.runningRequest ||
            this.queuedRequest ||
            this.asyncPrepareCount > 0 ||
            this.pendingCommit ||
            this.commitTimer !== null);
    }
    clearQueued() {
        this.queuedRequest = null;
    }
    cancelPendingCommit() {
        if (this.commitTimer !== null) {
            window.clearTimeout(this.commitTimer);
            this.commitTimer = null;
        }
        this.pendingCommit = null;
    }
    async drainAndClearQueued() {
        await this.prepareChain.catch(() => undefined);
        while (this.runningRequest || this.queuedRequest) {
            if (!this.runningRequest && this.queuedRequest) {
                this.startNextRequest();
            }
            const runningRequest = this.runningRequest;
            if (!runningRequest) {
                continue;
            }
            await runningRequest;
            if (this.runningRequest === runningRequest) {
                this.runningRequest = null;
            }
        }
    }
    async flushPendingCommit() {
        if (this.commitTimer !== null) {
            window.clearTimeout(this.commitTimer);
            this.commitTimer = null;
        }
        await this.drainAndClearQueued();
        const commit = this.pendingCommit;
        this.pendingCommit = null;
        if (commit) {
            await commit();
        }
    }
    reset() {
        this.clearQueued();
        this.cancelPendingCommit();
    }
    enqueuePrepare(request, startedPrepare) {
        this.asyncPrepareCount += 1;
        this.prepareChain = this.prepareChain
            .catch(() => undefined)
            .then(() => this.runPrepareStep(request, startedPrepare))
            .finally(() => {
            this.asyncPrepareCount = Math.max(0, this.asyncPrepareCount - 1);
        });
    }
    async runPrepareStep(request, startedPrepare) {
        try {
            const shouldContinue = startedPrepare
                ? await startedPrepare
                : request.prepare
                    ? await request.prepare()
                    : true;
            if (shouldContinue === false) {
                this.clearMatchingCompileContext(request);
                return;
            }
        }
        catch (error) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            request.onError?.(normalizedError);
            this.clearMatchingCompileContext(request);
            return;
        }
        this.finishLocalPrepare(request);
    }
    finishLocalPrepare(request) {
        if (!this.isRequestActive(request)) {
            this.clearMatchingCompileContext(request);
            return;
        }
        request.render?.();
        this.queuedRequest = {
            ...request,
            prepare: undefined
        };
        this.startNextRequest();
    }
    startNextRequest() {
        if (this.runningRequest || !this.queuedRequest) {
            return;
        }
        const request = this.queuedRequest;
        this.queuedRequest = null;
        const runningRequest = this.processRequest(request);
        this.runningRequest = runningRequest;
        void runningRequest.finally(() => {
            if (this.runningRequest === runningRequest) {
                this.runningRequest = null;
            }
            this.startNextRequest();
        });
    }
    async processRequest(request) {
        if (!this.isRequestActive(request)) {
            this.clearMatchingCompileContext(request);
            return;
        }
        await this.waitForNextPaint();
        if (!this.isRequestActive(request)) {
            this.clearMatchingCompileContext(request);
            return;
        }
        let shouldCompile;
        try {
            shouldCompile = await request.run();
        }
        catch (error) {
            const normalizedError = error instanceof Error ? error : new Error(String(error));
            request.onError?.(normalizedError);
            this.clearMatchingCompileContext(request);
            return;
        }
        if (shouldCompile === false) {
            this.clearMatchingCompileContext(request);
            return;
        }
        if (!this.isRequestActive(request)) {
            this.clearMatchingCompileContext(request);
            return;
        }
        this.requestLiveCompile(request);
    }
    isRequestActive(request) {
        return request.isActive ? request.isActive() : true;
    }
    requestLiveCompile(request) {
        const compileRequest = this.resolveCompileRequest(request);
        if (!compileRequest) {
            return;
        }
        const fm = window.fontManager;
        const currentFont = fm?.currentFont;
        if (!fm || !currentFont) {
            return;
        }
        fm.setEditingCompileContext(compileRequest.changeSource, compileRequest.editType);
        currentFont.requestRecompileWithoutDataChange({
            compileContext: {
                changeSource: compileRequest.changeSource,
                editType: compileRequest.editType,
                dataFreshnessMode: KEYBOARD_PREVIEW_DATA_FRESHNESS_MODE
            }
        });
        window.autoCompileManager?.checkAndSchedule?.();
        this.clearMatchingCompileContext(request);
    }
    clearMatchingCompileContext(request) {
        const compileRequest = this.resolveCompileRequest(request);
        if (!compileRequest) {
            return;
        }
        const fm = window.fontManager;
        if (!fm) {
            return;
        }
        if (fm.lastChangeSource === compileRequest.changeSource &&
            fm.lastEditType === compileRequest.editType) {
            fm.clearEditingCompileContext();
        }
    }
    resolveCompileRequest(request) {
        if (!request.compile) {
            return null;
        }
        return typeof request.compile === 'function'
            ? request.compile()
            : request.compile;
    }
    async waitForNextPaint() {
        await new Promise((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => resolve());
            });
        });
    }
}
